/* eslint-disable react/forbid-prop-types */
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import USBAccountNumberInput from '@usb-ui-tools/react-forms-input-account-number'
import USBBusinessNameInput from '@usb-ui-tools/react-forms-input-business-name'
import USBCodeInput from '@usb-shield/react-forms-input-code'
import { BlockLayout } from '@usb-ui-tools/react-billpay-layout'
import { isOLBOrIpadApp } from '../../../../utils/browserutils'
import getUSBExBillerLogo from '../../../../utils/extBillerLogo'
import { useMultilingualService } from '../../../../service/multilingualService'
import {
  getFieldErrorMessage,
  incrementErrorCountsForFieldLevelError
} from '../../../../utils/errorMapping'
import './details.scss'

/**
 * Returns details information
 * @method
 * @param {Number} accountNumberErrorCount - to render the error
 * @param {Object} accountNumberRef - referance of account number element
 * @param {Function} handleStatusUpdate - handler for input update
 * @param {Function} setNickName - to set nick name
 * @param {Function} setAccountNumber - to set account number
 * @param {Object} intl - locale of DashboardNearbyMarchants
 * @returns {HTMLElement} Returns segment with details information
 */
const Details = ({
  accountNumberRef,
  handleStatusUpdate,
  setAccountNumber,
  accountNumberErrorCount,
  setNickName,
  billerName,
  fieldLevelErrorData,
  zipCodeInputRef,
  zipCodeErrorCount,
  setZipCode,
  billerZipRequired,
  billerLogoUrl,
  accountNumber,
  zipCode,
  nickName
}) => {
  const { DETAILS, CONTACT } = useMultilingualService('addBillSearch')

  useEffect(() => {
    accountNumberRef?.current
      ?.querySelector('input')
      ?.classList?.add('glance_masked')
  }, [accountNumberRef])

  return (
    <div className="details">
      <BlockLayout
        actAs="CardChild"
        contentOnTop={
          <p className="usb-tg-body-medium usb-c-gray-80">
            {DETAILS?.SEARCH_DETAILS}
          </p>
        }
        id="add-bill-search-details"
      >
        <div style={{ display: 'flex' }}>
          {billerLogoUrl && (
            <img
              name="logo"
              src={getUSBExBillerLogo(billerLogoUrl)}
              alt="biller logo"
              aria-hidden
              style={{ height: '2rem', width: '2rem' }}
            />
          )}

          <h1
            id="addbillinfo-heading"
            aria-label={billerName}
            style={{ margin: 'auto 10px' }}
            className="usb-tg-header-medium usb-c-gray-80"
          >
            {billerName}
          </h1>
        </div>
        <div
          style={{
            borderBottom: '1px solid #CDCDD3'
          }}
        />

        <div
          style={{
            paddingLeft: '2px',
            width: isOLBOrIpadApp() ? '75%' : 'inherit'
          }}
        >
          <USBAccountNumberInput
            id="add-bill-search-accno-input"
            ref={accountNumberRef}
            inputName="account-number"
            labelText={DETAILS?.SEARCH_ACC_NUMBER}
            statusUpdateCallback={(inputObj) => {
              handleStatusUpdate(inputObj)
              const { inputValue } = inputObj

              setAccountNumber(inputValue)
            }}
            errorMessages={{
              default: getFieldErrorMessage(
                DETAILS?.SEARCH_ACCOUNT_ERROR,
                fieldLevelErrorData,
                'accountnumber',
                'addEditErrors'
              ),
              112: getFieldErrorMessage(
                DETAILS?.SEARCH_FILTER_ACCOUNT_ERROR,
                fieldLevelErrorData,
                'accountnumber',
                'addEditErrors'
              ),
              114: getFieldErrorMessage(
                DETAILS?.SEARCH_FILTER_ACCOUNT_ERROR,
                fieldLevelErrorData,
                'accountnumber',
                'addEditErrors'
              )
            }}
            showErrorMessage="every"
            showFilterErrors
            dynamicErrorCount={incrementErrorCountsForFieldLevelError(
              fieldLevelErrorData,
              'accountnumber',
              accountNumberErrorCount
            )}
            initValue={accountNumber}
          />
          <USBBusinessNameInput
            id="add-bill-search-name-input"
            inputName="username"
            pattern="[^a-zA-Z0-9 ,.'-]"
            labelText={DETAILS?.SEARCH_NICK_NAME}
            statusUpdateCallback={(inputObj) => {
              const { inputValue } = inputObj

              setNickName(inputValue)
            }}
            errorMessages={{
              default: `${DETAILS?.SEARCH_NICK_NAME_ERROR}`
            }}
            isOptional
            type="first"
            dynamicValue={nickName}
          />
          {billerZipRequired && (
            <USBCodeInput
              id="add-bill-search-zipcode-input"
              type="zip-code-plus-four"
              ref={zipCodeInputRef}
              inputName="zip-code"
              labelText={CONTACT?.ZIPCODE}
              statusUpdateCallback={(inputObj) => {
                handleStatusUpdate(inputObj)
                setZipCode(inputObj.inputValue)
              }}
              errorMessages={{
                213: CONTACT?.ZIPCODE_ERROR_213,
                221: CONTACT?.ZIPCODE_ERROR_221,
                222: CONTACT?.ZIPCODE_ERROR_222,
                224: CONTACT?.ZIPCODE_ERROR_224,
                default: getFieldErrorMessage(
                  CONTACT?.ZIPCODE_ERROR_DEFAULT,
                  fieldLevelErrorData,
                  'zipcode',
                  'addEditErrors'
                )
              }}
              showErrorMessage="every"
              showFilterErrors
              dynamicErrorCount={incrementErrorCountsForFieldLevelError(
                fieldLevelErrorData,
                'zipcode',
                zipCodeErrorCount
              )}
              dynamicValue={zipCode}
            />
          )}
        </div>
      </BlockLayout>
    </div>
  )
}

Details.propTypes = {
  accountNumberErrorCount: PropTypes.number.isRequired,
  accountNumberRef: PropTypes.objectOf(PropTypes.any).isRequired,
  handleStatusUpdate: PropTypes.func.isRequired,
  setAccountNumber: PropTypes.func.isRequired,
  setNickName: PropTypes.func.isRequired,
  fieldLevelErrorData: PropTypes.objectOf(PropTypes.any).isRequired,
  billerName: PropTypes.string.isRequired,
  zipCodeInputRef: PropTypes.objectOf(PropTypes.any).isRequired,
  zipCodeErrorCount: PropTypes.number.isRequired,
  setZipCode: PropTypes.func.isRequired,
  billerZipRequired: PropTypes.bool.isRequired,
  billerLogoUrl: PropTypes.string.isRequired,
  accountNumber: PropTypes.string.isRequired,
  zipCode: PropTypes.string.isRequired,
  nickName: PropTypes.string.isRequired
}

export default Details
